// Pieces shared by the settings screens: the settings tab strip from the prototype and the page head.
import { MATRIX, type PermissionCode } from "@visionx/contracts";
import type { ReactNode } from "react";
import { NavLink, useLocation } from "react-router";
import { isApiError } from "../api/problem";
import { Alert } from "../ui/bits";
import { Icon } from "../ui/icons";

export const SETTINGS_TABS: readonly { label: string; to: string; sprint?: number | undefined }[] = [
  { label: "General", to: "/settings/general" },
  { label: "Branches", to: "/settings/branches" },
  { label: "Users", to: "/settings/users" },
  // kiosk, today, appointments: the Zoho Bookings mapping (UC-APPOINTMENTS-001) and the consent purposes (UC-GOVERNANCE-016)
  { label: "Appointments", to: "/settings/appointments" },
  { label: "Consent", to: "/settings/consent" },
  // end kiosk, today, appointments
  { label: "Accounting & sync", to: "/settings/accounting", sprint: 9 },
  { label: "CRM", to: "/settings/crm", sprint: 10 },
  { label: "Medical aid", to: "/settings/medical-aid" }, // built in Sprint 2 (D64): the read-only scheme reference
  { label: "Markup & fitting", to: "/settings/markups", sprint: 4 },
  { label: "ICD-10", to: "/settings/icd10", sprint: 3 },
  // Sprint 6 brings one default template per document type and no settings; the per-branch overrides, printers and the print
  // bridge are Sprint 13 (spec/SPRINT-PLAN.md; BR-TENANCY-032).
  { label: "Documents", to: "/settings/documents", sprint: 13 },
  { label: "Printing & hardware", to: "/settings/printing", sprint: 13 },
  { label: "Audit log", to: "/settings/audit-log" },
  { label: "Data take-on", to: "/settings/takeon", sprint: 4 },
];

export function SettingsTabs() {
  const loc = useLocation();
  // Fourteen tabs: the strip wraps onto a second line rather than scrolling sideways, so every tab is in view at any width.
  return (
    <div className="tabs wrap">
      {SETTINGS_TABS.map((t) => (
        <NavLink key={t.to} to={t.to} className={loc.pathname.startsWith(t.to) ? "active" : ""} title={t.sprint ? `Sprint ${t.sprint}` : undefined}>{t.label}</NavLink>
      ))}
    </div>
  );
}

export function PageHead({ title, sub, actions }: { title: ReactNode; sub?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

const ROLE_LABEL: Record<string, string> = { Owner: "Owner", Admin: "Admin", Optometrist: "Optometrist", Dispenser: "Dispenser", FrameConsultant: "Frame consultant", Receptionist: "Receptionist" };
export const roleLabel = (r: string): string => ROLE_LABEL[r] ?? r;

/**
 * Grants the API makes by designation rather than role — a Branch owner is a flag on an assignment, not a role, so the matrix
 * cannot list it. Owner decision, 15 Sep 2026: Branch owners read the Control log. Copy only; the screens gate on the session's
 * `permissions`, never on a role name.
 */
const DESIGNATION_GRANTS: Partial<Record<PermissionCode, readonly string[]>> = { "governance.control_log.read": ["Branch owner"] };

/** Who the API grants a code — the matrix's roles, then any designation — for "Owner, Admin and Branch owner only" sentences. */
export function rolesFor(code: string | undefined): string[] {
  if (!code || !(code in MATRIX)) return [];
  return [...(MATRIX[code as PermissionCode] as readonly string[]).map(roleLabel), ...(DESIGNATION_GRANTS[code as PermissionCode] ?? [])];
}

export function rolesSentence(code: string | undefined): string {
  const roles = rolesFor(code);
  if (roles.length === 0) return "Not available to your role.";
  if (roles.length === 1) return `${roles[0]} only.`;
  return `${roles.slice(0, -1).join(", ")} and ${roles[roles.length - 1]} only.`;
}

/**
 * The inline no-access panel (design/screens/no-access.html) for a screen whose data a 403 refused. It names who may and
 * where access is granted, and claims nothing else: the route guard writes no audit entry on a refusal, so the prototype's
 * "This attempt is recorded in the audit log" and its timestamp are not shown (adversarial review 16 Sep 2026, L7).
 */
export function NoAccessPanel({ page, error, permission: needed }: { page: string; error?: unknown; permission?: PermissionCode | undefined }) {
  // The code a 403 named, or — for a screen opened by its address that sends nothing its user may not send — the one it needs.
  const permission = isApiError(error) && typeof error.meta.permission === "string" ? error.meta.permission : needed;
  return (
    <div className="panel" id="s-no-access">
      <div className="empty">
        <Icon name="lock" />
        <h2>You don't have access to {page}</h2>
        <p>{rolesSentence(permission)}</p>
        <div className="row c gap-8">
          <NavLink className="btn primary" to="/today">Back to Today</NavLink>
        </div>
        <span className="help">Access is granted under Settings › Users by an Owner or Admin.{permission ? ` Permission ${permission}.` : ""}</span>
      </div>
    </div>
  );
}

export function OfflineNote() {
  return <Alert tone="warn"><b>Offline.</b> Changes wait for a connection (cached data only).</Alert>;
}

/**
 * No branch is active — a new organisation without branches, a user assigned to none. A branch's day is then never asked for,
 * so the page says so instead of showing it as loading for good.
 */
export function NoActiveBranch({ what }: { what: string }) {
  return <Alert tone="warn"><b>No active branch.</b> Choose a branch in the top bar to see {what}. If none is offered, an Owner or Admin adds the branch in Settings › Branches or assigns you to one.</Alert>;
}
